import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Loader2 } from 'lucide-react';
import { handleError } from '@/utils/errorHandler';
import { SESSION_CHECK_INTERVAL_MS } from '@/constants/timings';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, session, loading, signOut } = useAuth();

  // Check for expired session
  useEffect(() => {
    if (session?.expires_at) {
      const expiresAtMs = session.expires_at * 1000; // expires_at is in seconds
      if (Date.now() > expiresAtMs) {
        signOut();
      }
    }
  }, [session, signOut]);

  // Periodic session health check — detect expiration while the route is active
  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();

        if (!currentSession) {
          handleError(new Error('Session health check: no session'), { level: 'warning', message: 'No active session found, signing out', operation: 'sessionHealthCheck' });
          await signOut();
          return;
        }

        if (currentSession.expires_at) {
          const expiresAtMs = currentSession.expires_at * 1000;
          if (Date.now() > expiresAtMs) {
            handleError(new Error('Session health check: session expired'), { level: 'warning', message: 'Session expired, signing out', operation: 'sessionHealthCheck' });
            await signOut();
          }
        }
      } catch (err) {
        handleError(err, { level: 'warning', message: 'Errore nel controllo di salute della sessione' });
      }
    }, SESSION_CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [signOut]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-surface-primary gradient-radial">
        <div className="flex flex-col items-center gap-5 animate-fade-in">
          <div className="relative">
            <div className="w-14 h-14 rounded-xl bg-linear-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-lg shadow-accent-violet/20">
              <span className="text-lg font-bold text-white">Z</span>
            </div>
            <div className="absolute -right-1 -bottom-1 w-6 h-6 rounded-full bg-surface-primary flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-accent-violet animate-spin" />
            </div>
          </div>
          <p className="text-sm font-semibold text-text-primary">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!user || !session) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
